import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkle, ArrowRight, FileText } from '@phosphor-icons/react';

/**
 * LatestInsightCard — Stage 5
 *
 * Calls GET /api/home/latest-insight. When a user has uploaded and analysed
 * at least one report, this surfaces 1–2 plain-English findings + a friendly
 * "ask Kindra more" CTA that seeds the chat composer. When there is no
 * insight to surface, the card hides entirely (no shell, no awkward state).
 *
 * Design intent:
 *   • Premium editorial card (warm paper, navy ink, teal accent).
 *   • Auto-hides on empty so first-time users don't see scaffolding.
 *   • Gives every uploaded report a daily-value moment on home.
 */
export const LatestInsightCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/home/latest-insight`);
        if (!cancelled) setData(d);
      } catch {
        /* silent — card just hides on error */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hide entirely until we know — avoids skeletons for empty-state users.
  if (!loaded) return null;
  if (!data?.has_insight) return null;

  // Stage 9 — render only plain-English fields. The legacy `headline` and
  // `findings` are kept on the response for back-compat but must NEVER be
  // rendered above-the-fold on Home (audit P2).
  const { document: doc, headline_plain, findings_plain = [], ask_prompt } = data;
  const filename = doc?.filename || 'your latest report';

  const askKindra = () => {
    // Seeds the chat composer with the suggested question.
    navigate('/chat', { state: { prefillMessage: ask_prompt || `What does ${filename} mean for me?` } });
  };

  const openDoc = () => {
    if (doc?.id) navigate(`/documents/${doc.id}`);
  };

  return (
    <AnimatePresence>
      <motion.div
        key="latest-insight"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative rounded-2xl border border-[#ECE8E1] bg-white overflow-hidden"
        data-testid="latest-insight-card"
      >
        {/* Eyebrow / header row */}
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={12} weight="fill" className="text-[#2FB3A0]" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Your latest insight
            </span>
          </div>
          {headline_plain && (
            <p
              className="font-serif text-[20px] sm:text-[22px] leading-tight tracking-[-0.005em] text-[#0E1C2E]"
              data-testid="latest-insight-headline"
            >
              {headline_plain}
            </p>
          )}
          {/* Source line — clickable, leads to the document detail */}
          <button
            type="button"
            onClick={openDoc}
            className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 hover:text-[#0E1C2E] transition-colors"
            data-testid="latest-insight-doc-link"
          >
            <FileText size={11} weight="duotone" className="text-slate-400" />
            <span className="truncate max-w-[260px]">From <em className="not-italic font-medium">{filename}</em></span>
          </button>
        </div>

        {/* Findings */}
        {findings_plain.length > 0 && (
          <div className="px-5 pb-4 space-y-2.5" data-testid="latest-insight-findings">
            {findings_plain.map((f, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="mt-2 w-1 h-1 rounded-full bg-[#2FB3A0] flex-shrink-0" />
                <p className="text-[13.5px] text-[#0E1C2E] leading-relaxed">{f}</p>
              </div>
            ))}
          </div>
        )}

        {/* CTA strip — paper-tinted footer */}
        <div className="px-5 py-3 bg-[#FBFAF6] border-t border-[#ECE8E1]">
          <button
            type="button"
            onClick={askKindra}
            className="w-full inline-flex items-center justify-between gap-2 group"
            data-testid="latest-insight-ask-cta"
          >
            <span className="text-[13px] font-medium text-[#0E1C2E] leading-snug">
              {ask_prompt || `Ask Kindra what this means for you`}
            </span>
            <ArrowRight
              size={14}
              weight="bold"
              className="text-[#2FB3A0] group-hover:translate-x-0.5 transition-transform flex-shrink-0"
            />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LatestInsightCard;
